import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const GOLD = "#F5A623";
const DARK = "#0F172A";
const SLATE = "#475569";
const LIGHT = "#F8FAFC";
const BORDER = "#CBD5E1";

const safe = (value, fallback = "-") => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return safe(value);
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return safe(value);
  }
};

const titleCase = (value) => {
  if (!value) return "-";

  return String(value)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
};

const cleanValue = (value) => {
  if (Array.isArray(value)) {
    return value.length
      ? value.map((item) => safe(item)).join(", ")
      : "-";
  }

  if (
    typeof value === "object" &&
    value !== null
  ) {
    return Object.entries(value)
      .map(
        ([key, val]) =>
          `${titleCase(key)}: ${safe(val)}`
      )
      .join("\n");
  }

  return safe(value);
};

const addPageHeader = (doc) => {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  doc.setFillColor(DARK);
  doc.rect(0, 0, pageWidth, 22, "F");

  doc.setTextColor("#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);

  doc.text(
    "CET 2027",
    14,
    14
  );

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");

  doc.text(
    "Corporate Entrance Test",
    pageWidth - 14,
    14,
    {
      align: "right",
    }
  );

  doc.setDrawColor(GOLD);
  doc.setLineWidth(1.2);

  doc.line(
    14,
    24,
    pageWidth - 14,
    24
  );
};

const addPageFooter = (doc) => {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  doc.setDrawColor(BORDER);
  doc.setLineWidth(0.3);

  doc.line(
    14,
    pageHeight - 15,
    pageWidth - 14,
    pageHeight - 15
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(SLATE);

  doc.text(
    "CET 2027 Application Form",
    14,
    pageHeight - 8
  );

  doc.text(
    `Page ${doc.internal.getNumberOfPages()}`,
    pageWidth - 14,
    pageHeight - 8,
    {
      align: "right",
    }
  );
};

const addSectionTitle = (
  doc,
  title,
  y
) => {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  doc.setFillColor(LIGHT);

  doc.roundedRect(
    14,
    y,
    pageWidth - 28,
    10,
    2,
    2,
    "F"
  );

  doc.setFillColor(GOLD);

  doc.rect(
    14,
    y,
    3,
    10,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(DARK);

  doc.text(
    title,
    21,
    y + 6.7
  );

  return y + 15;
};

const addKeyValueTable = (
  doc,
  rows,
  startY
) => {
  autoTable(doc, {
    startY,
    body: rows.map(([label, value]) => [
      label,
      cleanValue(value),
    ]),

    theme: "grid",

    styles: {
      font: "helvetica",
      fontSize: 8.5,
      textColor: DARK,
      cellPadding: 4,
      lineColor: BORDER,
      lineWidth: 0.25,
      overflow: "linebreak",
      valign: "middle",
    },

    columnStyles: {
      0: {
        cellWidth: 52,
        fontStyle: "bold",
        textColor: SLATE,
      },

      1: {
        cellWidth: "auto",
      },
    },

    headStyles: {
      fillColor: DARK,
      textColor: "#FFFFFF",
      fontStyle: "bold",
    },

    alternateRowStyles: {
      fillColor: "#FCFCFD",
    },

    margin: {
      left: 14,
      right: 14,
      bottom: 22,
      top: 32,
    },

    didDrawPage: () => {
      addPageHeader(doc);
      addPageFooter(doc);
    },
  });

  return doc.lastAutoTable.finalY + 10;
};

export async function generateApplicationPDF(
  application
) {
  if (!application) {
    throw new Error(
      "Application data is missing."
    );
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  /*
   * =====================================================
   * DATA
   * =====================================================
   */

  const personal =
    application.personal || {};

  /*
   * IMPORTANT:
   * Email and phone are stored by backend
   * inside application.contact
   */
  const contact =
    application.contact || {};

  const academic =
    application.academic || {};

  const address =
    application.address || {};

  const preferences =
    application.preferences || {};

  const declarations =
    application.declarations || {};

  const payment =
    application.payment || {};

  /*
   * =====================================================
   * PAGE SETTINGS
   * =====================================================
   */

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  /*
   * =====================================================
   * PAGE 1 - COVER / APPLICATION SUMMARY
   * =====================================================
   */

  addPageHeader(doc);

  let y = 34;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(DARK);

  doc.text(
    "CET 2027",
    14,
    y
  );

  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(SLATE);

  doc.text(
    "Corporate Entrance Test — Application Form",
    14,
    y
  );

  /*
   * Application number box
   */

  y += 13;

  doc.setFillColor(DARK);

  doc.roundedRect(
    14,
    y,
    pageWidth - 28,
    28,
    3,
    3,
    "F"
  );

  doc.setTextColor("#CBD5E1");
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");

  doc.text(
    "APPLICATION NUMBER",
    21,
    y + 9
  );

  doc.setTextColor(GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);

  doc.text(
    safe(
      application.applicationNumber
    ),
    21,
    y + 20
  );

  doc.setTextColor("#FFFFFF");
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");

  doc.text(
    "CET 2027",
    pageWidth - 21,
    y + 16,
    {
      align: "right",
    }
  );

  y += 38;

  /*
   * =====================================================
   * CANDIDATE SUMMARY
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "Candidate Summary",
    y
  );

  y = addKeyValueTable(
    doc,
    [
      [
        "Full Name",
        personal.fullName ||
          application.fullName ||
          "-",
      ],

      /*
       * EMAIL:
       * Primary source = application.contact.email
       */
      [
        "Email",
        contact.email ||
          application.email ||
          personal.email ||
          "-",
      ],

      /*
       * PHONE:
       * Primary source = application.contact.phone
       */
      [
        "Phone",
        contact.phone ||
          application.phone ||
          personal.phone ||
          "-",
      ],

      [
        "Application Status",
        application.status ===
        "submitted"
          ? "Submitted"
          : titleCase(
              application.status
            ),
      ],

      [
        "Application Date",
        formatDateTime(
          application.submittedAt ||
            application.createdAt
        ),
      ],
    ],
    y
  );

  /*
   * =====================================================
   * PAYMENT SUMMARY
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "Payment Summary",
    y
  );

  y = addKeyValueTable(
    doc,
    [
      [
        "Payment Status",
        titleCase(
          payment.status
        ),
      ],

      [
        "Amount",
        payment.amount
          ? `INR ${payment.amount}`
          : "-",
      ],

      [
        "Payment ID",
        payment.paymentId ||
          payment.razorpayPaymentId ||
          "-",
      ],

      [
        "Order ID",
        payment.orderId ||
          payment.razorpayOrderId ||
          "-",
      ],

      [
        "Payment Date",
        formatDateTime(
          payment.paidAt ||
            payment.createdAt
        ),
      ],
    ],
    y
  );

  /*
   * =====================================================
   * PAGE 2+ - PERSONAL DETAILS
   * =====================================================
   */

  doc.addPage();

  addPageHeader(doc);

  y = 34;

  y = addSectionTitle(
    doc,
    "1. Personal Details",
    y
  );

  y = addKeyValueTable(
    doc,
    Object.entries(personal).map(
      ([key, value]) => [
        titleCase(key),
        value,
      ]
    ),
    y
  );

  /*
   * =====================================================
   * CONTACT DETAILS
   * =====================================================
   *
   * Email and phone are also included separately
   * here because backend stores them in `contact`.
   *
   * This guarantees they appear in the complete
   * application PDF as well.
   */

  y = addSectionTitle(
    doc,
    "Contact Details",
    y
  );

  y = addKeyValueTable(
    doc,
    [
      [
        "Email",
        contact.email ||
          application.email ||
          personal.email ||
          "-",
      ],

      [
        "Phone",
        contact.phone ||
          application.phone ||
          personal.phone ||
          "-",
      ],
    ],
    y
  );

  /*
   * =====================================================
   * ACADEMIC
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "2. Academic Details",
    y
  );

  y = addKeyValueTable(
    doc,
    Object.entries(academic).map(
      ([key, value]) => [
        titleCase(key),
        value,
      ]
    ),
    y
  );

  /*
   * =====================================================
   * ADDRESS
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "3. Address Details",
    y
  );

  y = addKeyValueTable(
    doc,
    Object.entries(address).map(
      ([key, value]) => [
        titleCase(key),
        value,
      ]
    ),
    y
  );

  /*
   * =====================================================
   * PREFERENCES
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "4. Preferences",
    y
  );

  y = addKeyValueTable(
    doc,
    Object.entries(preferences).map(
      ([key, value]) => [
        titleCase(key),
        value,
      ]
    ),
    y
  );

  /*
   * =====================================================
   * DECLARATIONS
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "5. Declarations",
    y
  );

  y = addKeyValueTable(
    doc,
    Object.entries(declarations).map(
      ([key, value]) => [
        titleCase(key),
        value,
      ]
    ),
    y
  );

  /*
   * =====================================================
   * FINAL APPLICATION INFORMATION
   * =====================================================
   */

  y = addSectionTitle(
    doc,
    "Application Information",
    y
  );

  addKeyValueTable(
    doc,
    [
      [
        "Application Number",
        application.applicationNumber,
      ],

      [
        "Status",
        titleCase(
          application.status
        ),
      ],

      [
        "Submitted At",
        formatDateTime(
          application.submittedAt
        ),
      ],

      [
        "Generated On",
        formatDateTime(
          new Date()
        ),
      ],
    ],
    y
  );

  /*
   * =====================================================
   * FINAL NOTE
   * =====================================================
   */

  const finalPage =
    doc.internal.getNumberOfPages();

  doc.setPage(finalPage);

  let finalY =
    doc.lastAutoTable?.finalY ||
    40;

  if (
    finalY >
    pageHeight - 50
  ) {
    doc.addPage();

    addPageHeader(doc);

    finalY = 35;
  }

  doc.setFillColor("#FFFBEB");

  doc.roundedRect(
    14,
    finalY + 5,
    pageWidth - 28,
    25,
    3,
    3,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(DARK);

  doc.text(
    "Important:",
    20,
    finalY + 14
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(SLATE);

  doc.text(
    "Please retain this application form and application number",
    20,
    finalY + 20
  );

  doc.text(
    "for future reference.",
    20,
    finalY + 24
  );

  /*
   * =====================================================
   * ADD FOOTER TO ALL PAGES
   * =====================================================
   */

  const totalPages =
    doc.internal.getNumberOfPages();

  for (
    let page = 1;
    page <= totalPages;
    page++
  ) {
    doc.setPage(page);

    addPageFooter(doc);
  }

  /*
   * =====================================================
   * DOWNLOAD
   * =====================================================
   */

  const applicationNumber =
    application.applicationNumber ||
    "application";

  const fileName =
    `CET-2027-Application-${applicationNumber}.pdf`;

  doc.save(fileName);

  return fileName;
}